import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useDispatch, useSelector } from 'react-redux';
import { MARKETS_SLUGS_MAP } from 'utils/transform';
import { EventsActions } from 'store/actions';
import { DetectMarketSelectors, EventsSelectors } from 'store/selectors';
import { useLoader } from 'hooks';
import useAuthProps from 'hooks/useAuthProps';
import UserEventCard from 'components/common/EventCard/UserEventCard';
import useLoggedOutFavoriteEvent from 'hooks/useLoggedOutFavoriteEvent';
import RenderComponent from 'components/common/RenderComponent';
import { isFetchingStates } from 'store/utils';
import { Container, Grid, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ArrowLeftIcon from 'components/svg/icon-arrow-left-24px.svg';
import ArrowRightIcon from 'components/svg/icon-arrow-right-24px.svg';
import { useRouter } from 'next/router';

const useStyles = makeStyles()((theme) => ({
  containerWrapper: {
    backgroundColor: theme.palette.custom.gray.extraLight,
    '@media (max-width: 740px)': {
      paddingLeft: 19,
      paddingRight: 19,
    },
  },
  swiperContainerWrapper: {
    padding: '64px 80px',
    '@media (max-width: 850px)': {
      padding: '64px 0px',
      maxWidth: 736,
      marginRight: 'auto',
      marginLeft: 'auto',
    },
    '@media (max-width: 740px)': {
      padding: 0,
      paddingTop: 64,
      maxWidth: 736,
      marginRight: 'auto',
      marginLeft: 'auto',
    },
  },
  arrowLeftWrapper: {
    height: 16,
    position: 'relative',
    top: 'calc(527px / 2 + 53px )',
    left: -80,
    zIndex: 10,
  },
  arrowRightWrapper: {
    height: 16,
    position: 'relative',
    zIndex: 10,
    right: -80,
    top: 'calc(527px / 2 + 53px )',
    marginLeft: 'auto',
  },
  eventWrapper: {
    width: 352,
    minHeight: 399,
    marginTop: 17,
    marginBottom: 80,
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (max-width: 740px)': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  swiperSlide: {
    '&&&': {
      display: 'flex',
      height: 'auto',
    },
  },
  swiperWrapper: {
    maxWidth: '100%',
    '@media (max-width: 740px)': {
      maxWidth: 360,
      margin: '0 auto',
    },
  },
}));
const FavoriteEventsSwiper: React.FC<React.PropsWithChildren<unknown>> = () => {
  // const [t] = useTranslation('public');
  const dispatch = useDispatch();
  const market = useSelector(DetectMarketSelectors.getSelectedMarket);
  const { isAuthenticated, onLogin } = useAuthProps();
  const market_slug = useMemo(() => MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP], [market]);
  const { loggedOutFavoriteEvent, setLoggedOutFavoriteEvent, handleResetLoggedOutFavoriteEvent } =
    useLoggedOutFavoriteEvent();
  const less850 = useMediaQuery('(max-width: 850px)');
  const less1190 = useMediaQuery('(max-width: 1191px)');
  const less740 = useMediaQuery('(max-width: 740px)');
  const sliderRef = useRef<any>();
  const router = useRouter();
  const currentEventId = Number(router.query.eventId);
  const { classes } = useStyles();

  const queryParams = useMemo(
    () => ({
      exclude_hidden_events: true,
      ordering: isAuthenticated ? '-recommendation_rating' : 'session_date',
      market_slug,
      page_size: 12,
    }),
    [isAuthenticated, market_slug],
  );

  const handleToggleFavoriteEvent = useCallback(
    (eventId: any) => dispatch(EventsActions.toggleFavoriteEvent.request({ id: eventId })),
    [dispatch],
  );

  const stateSelector = useMemo(() => EventsSelectors.contextualEventsState(), []);
  const { state } = useLoader({
    actions: EventsActions.getContextualEvents,
    stateSelector,
    options: {
      queryParams,
    },
  });

  const eventList = useMemo(
    () => state.entities.filter((event) => event.id !== currentEventId),
    [currentEventId, state.entities],
  );

  const slidesPerView = useMemo(() => {
    if (less740 || eventList.length === 1) return 1;
    if (less1190 || eventList.length === 2) return 2;
    return 3;
  }, [less740, eventList.length, less1190]);

  const spaceBetween = useMemo(() => {
    if (less740) return 32;
    if (less1190) return 32;
    return 28;
  }, [less740, less1190]);

  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (slidesPerView === eventList.length) setIsEnd(true);
  }, [eventList.length, slidesPerView]);

  const handleSlideChange = () => {
    setIsEnd(false);
    setIsStart(false);
    if (sliderRef.current.activeIndex === 0) setIsStart(true);
    if (sliderRef.current.activeIndex >= eventList.length - slidesPerView) setIsEnd(true);
  };

  if (state.isFetched && eventList.length < 1) return null;

  return (
    <div className={classes.containerWrapper}>
      <Container maxWidth="lg" disableGutters>
        <div className={classes.swiperContainerWrapper}>
          <RenderComponent
            isLoading={isFetchingStates(state)}
            renderContent={() => (
              <>
                <Typography variant="h5">More events for you</Typography>
                <Grid container>
                  {!less850 && (
                    <Grid className={classes.arrowLeftWrapper} item>
                      <ArrowLeftIcon
                        opacity={isStart ? 0.4 : 1}
                        onClick={() => !isStart && sliderRef?.current?.slidePrev()}
                      />
                    </Grid>
                  )}
                  {!less850 && (
                    <Grid item className={classes.arrowRightWrapper}>
                      <ArrowRightIcon
                        opacity={isEnd ? 0.4 : 1}
                        disabled
                        onClick={() => !isEnd && sliderRef.current?.slideNext()}
                      />
                    </Grid>
                  )}
                  <Grid className={classes.swiperWrapper} item>
                    <Swiper
                      onSwiper={(e: any) => {
                        sliderRef.current = e;
                      }}
                      spaceBetween={spaceBetween}
                      slidesPerView={slidesPerView}
                      centeredSlides={false}
                      onSlideChange={handleSlideChange}
                      grabCursor
                      // slidesOffsetBefore={less740 ? 0 : -60}
                      // slidesOffsetAfter={80}
                      keyboard={{
                        enabled: true,
                      }}
                      pagination={{
                        clickable: true,
                      }}
                      modules={[Keyboard, Navigation, Pagination]}
                    >
                      {eventList.map((event: any) => (
                        <SwiperSlide className={classes.swiperSlide}>
                          <div className={classes.eventWrapper}>
                            <UserEventCard
                              event={event}
                              isFavoriteOfflineToggled={loggedOutFavoriteEvent === event.id}
                              onLogin={onLogin}
                              onToggleFavorite={isAuthenticated ? handleToggleFavoriteEvent : setLoggedOutFavoriteEvent}
                              onCloseFavoritePopUp={handleResetLoggedOutFavoriteEvent}
                              noLocationLiveIcons
                              dateFirst
                              padding={16}
                            />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Grid>
                </Grid>
              </>
            )}
          />
        </div>
      </Container>
    </div>
  );
};

export default FavoriteEventsSwiper;
